import Button from '@mui/material/Button';
import { Avatar } from '@mui/material';
import useQuestionStyles from './useQuestionStyles';
import { getMetaData } from '../../../../tool';
import { Question } from '../../../../types';
import useLayoutStyles from '../../../layout/useLayoutStyles';
import usePageLink from '../../../../hooks/useQuestion';
import useGlobalStyles from '../../../useGlobalStyles';
import { useAppSelector } from '../../../../redux/hooks';
import { ulistSelector } from '../../../../redux/slices/userSlice';

/**
 * Interface representing the props for the Question component.
 *
 * q - The question object containing details about the question.
 */
interface QuestionProps {
  q: Question;
}

/**
 * Question component renders the details of a question including its title, tags, author, answers, and views.
 * Clicking on the component triggers the handleAnswer function,
 * and clicking on a tag triggers the clickTag function.
 *
 * @param q - The question object containing question details.
 */
const QuestionView = ({ q }: QuestionProps) => {
  const { classes } = useQuestionStyles();
  const { classes: layoutClasses } = useLayoutStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const users = useAppSelector(ulistSelector);
  const questionAuthor = q.askedByUser && users?.find(user => user._id === q.askedByUser?._id);
  const { handleAnswer, handleUser, clickTag } = usePageLink();

  return (
    <div
      className={`${classes.question} ${layoutClasses.rightPadding}`}
      onClick={() => {
        if (q._id) {
          handleAnswer(q._id);
        }
      }}>
      <div className={classes.postStats}>
        <div className={globalClasses.p}>
          {q.answers.length || 0} {q.answers.length === 1 ? 'answer' : 'answers'}
        </div>
        <div className={globalClasses.p}>
          {q.views.length} {q.views.length === 1 ? 'view' : 'views'}
        </div>
      </div>
      <div className={classes.questionMid}>
        <div className={classes.postTitle}>{q.title}</div>
        <div className={classes.questionTags}>
          {q.tags.map((tag, idx) => (
            <Button
              key={idx}
              className={classes.questionTagButton}
              onClick={e => {
                e.stopPropagation();
                clickTag(tag.name);
              }}>
              {tag.name}
            </Button>
          ))}
        </div>
      </div>
      <div
        className={classes.lastActivity}
        onClick={e => {
          e.stopPropagation();
          if (questionAuthor) handleUser(questionAuthor.username);
        }}>
        <Avatar
          className={globalClasses.userAvatar}
          src={`${process.env.PUBLIC_URL}/assets/profiles/${questionAuthor?.profilePicture}`}
        />
        <div className={classes.questionInfo}>
          <div
            className={`${classes.questionAuthor} ${!questionAuthor?.username && classes.deletedQuestionAuthor}`}>
            {questionAuthor?.username || 'Deleted User'}
          </div>
          <div className={classes.questionMeta}>{getMetaData(new Date(q.askDateTime))}</div>
        </div>
      </div>
    </div>
  );
};

export default QuestionView;
